import type { TFunction } from 'i18next'
import type { LatestWorkflowForEntityResponse } from '@/shared/hooks/api/workflows'
import {
  PRODUCT_LAW_KEYS,
  type ProductApplicabilityState,
  type ProductLawApplicabilityStatus,
  type ProductLawKey,
} from '@/shared/hooks/api/product'

export const PRODUCT_APPLICABILITY_DISCOVER_WORKFLOW_TYPE =
  'product_applicability_discover'

export type ProductDiscoverLogLine = {
  id: string
  text: string
}

export type ProductDiscoverLogSectionId = ProductLawKey | 'followUp' | 'summary'

export type ProductDiscoverLogSection = {
  id: ProductDiscoverLogSectionId
  lines: ProductDiscoverLogLine[]
}

type LawFirstPassStepResult = {
  law?: string
  skipped?: boolean
  status?: string | null
}

export type ProductDiscoverLawVerdict = {
  law: ProductLawKey
  skipped: boolean
  status: ProductLawApplicabilityStatus | null
}

const DISCOVER_LAW_ORDER: ProductLawKey[] = [...PRODUCT_LAW_KEYS]

function isProductLawKey(value: string | undefined): value is ProductLawKey {
  if (value == null) {
    return false
  }
  return (PRODUCT_LAW_KEYS as readonly string[]).includes(value)
}

function parseApplicabilityState(
  value: unknown
): ProductApplicabilityState | null {
  if (value == null || typeof value !== 'object') {
    return null
  }

  const record = value as Record<string, unknown>
  const hasAnyLaw = PRODUCT_LAW_KEYS.some((law) => law in record)
  if (!hasAnyLaw) {
    return null
  }

  return {
    machinery: (record.machinery ?? null) as ProductApplicabilityState['machinery'],
    rohs: (record.rohs ?? null) as ProductApplicabilityState['rohs'],
    emc: (record.emc ?? null) as ProductApplicabilityState['emc'],
    lvd: (record.lvd ?? null) as ProductApplicabilityState['lvd'],
    red: (record.red ?? null) as ProductApplicabilityState['red'],
    weee: (record.weee ?? null) as ProductApplicabilityState['weee'],
    fgas: (record.fgas ?? null) as ProductApplicabilityState['fgas'],
    ped: (record.ped ?? null) as ProductApplicabilityState['ped'],
  }
}

function emptyLawVerdicts(): Record<ProductLawKey, ProductDiscoverLawVerdict | null> {
  return {
    machinery: null,
    rohs: null,
    emc: null,
    lvd: null,
    red: null,
    weee: null,
    fgas: null,
    ped: null,
  }
}

type LogSectionBuckets = Record<
  ProductDiscoverLogSectionId,
  ProductDiscoverLogLine[]
>

function createLogSectionBuckets(): LogSectionBuckets {
  return {
    machinery: [],
    rohs: [],
    emc: [],
    lvd: [],
    red: [],
    weee: [],
    fgas: [],
    ped: [],
    followUp: [],
    summary: [],
  }
}

function lawSectionForStep(step: string): ProductLawKey | null {
  if (!step.endsWith('_first_pass')) {
    return null
  }
  const law = step.slice(0, -'_first_pass'.length)
  if (!isProductLawKey(law)) {
    return null
  }
  return law
}

export function buildProductDiscoverLogSections(
  workflow: LatestWorkflowForEntityResponse | undefined,
  translate: TFunction<'common'>
): ProductDiscoverLogSection[] {
  if (workflow == null || workflow.status === 'none') {
    return []
  }

  const buckets = createLogSectionBuckets()
  let lineIndex = 0

  const pushLine = (
    sectionId: ProductDiscoverLogSectionId,
    id: string,
    text: string
  ) => {
    buckets[sectionId].push({ id: `${id}-${lineIndex}`, text })
    lineIndex += 1
  }

  for (const event of workflow.events) {
    if (event.type === 'step.started') {
      const step = event.step ?? ''
      const lawSection = lawSectionForStep(step)
      if (lawSection != null) {
        pushLine(
          lawSection,
          step,
          translate('v2.pages.products.detail.discover.log.lawStarted', {
            law: translate(`v2.applicability.laws.${lawSection}`),
          })
        )
      } else if (step === 'sync_compliance_files') {
        pushLine(
          'followUp',
          step,
          translate('v2.pages.products.detail.discover.log.syncComplianceFiles')
        )
      } else if (step === 'provision_machinery_compliance') {
        pushLine(
          'followUp',
          step,
          translate(
            'v2.pages.products.detail.discover.log.provisionMachineryCompliance'
          )
        )
      }
      continue
    }

    if (event.type === 'llm.started') {
      const step = event.step ?? ''
      const lawSection = lawSectionForStep(step)
      if (lawSection != null) {
        pushLine(
          lawSection,
          `${step}-answering`,
          translate('v2.pages.products.detail.discover.log.lawAnswering', {
            law: translate(`v2.applicability.laws.${lawSection}`),
          })
        )
      }
      continue
    }

    if (event.type === 'step.completed') {
      continue
    }

    if (event.type === 'workflow.completed') {
      pushLine(
        'summary',
        'workflow-completed',
        translate('v2.pages.products.detail.discover.log.finished')
      )
      continue
    }

    if (event.type === 'workflow.failed') {
      const errorMessage =
        typeof event.error === 'string' && event.error.trim().length > 0
          ? event.error.trim()
          : translate('v2.pages.products.detail.discover.log.failed')
      pushLine('summary', 'workflow-failed', errorMessage)
    }
  }

  if (workflow.status === 'running' && lineIndex === 0) {
    pushLine(
      'summary',
      'running',
      translate('v2.pages.products.detail.discover.log.started')
    )
  }

  const sectionOrder: ProductDiscoverLogSectionId[] = [
    ...DISCOVER_LAW_ORDER,
    'followUp',
    'summary',
  ]

  return sectionOrder
    .map((id) => ({ id, lines: buckets[id] }))
    .filter((section) => section.lines.length > 0)
}

export function buildProductDiscoverLogLines(
  workflow: LatestWorkflowForEntityResponse | undefined,
  translate: TFunction<'common'>
): ProductDiscoverLogLine[] {
  return buildProductDiscoverLogSections(workflow, translate).flatMap(
    (section) => section.lines
  )
}

export function extractProductDiscoverVerdicts(
  workflow: LatestWorkflowForEntityResponse | undefined
): Record<ProductLawKey, ProductDiscoverLawVerdict | null> {
  const verdicts = emptyLawVerdicts()

  if (workflow == null || workflow.status === 'none') {
    return verdicts
  }

  for (const event of workflow.events) {
    if (event.type !== 'step.completed') {
      continue
    }

    const step = event.step ?? ''
    const lawFromStep = lawSectionForStep(step)
    if (lawFromStep == null) {
      continue
    }

    const result = (event.result ?? {}) as LawFirstPassStepResult
    const law = isProductLawKey(result.law) ? result.law : lawFromStep

    verdicts[law] = {
      law,
      skipped: result.skipped === true,
      status: (result.status as ProductLawApplicabilityStatus | null) ?? null,
    }
  }

  const completedEvent = workflow.events.find(
    (event) => event.type === 'workflow.completed'
  )
  const completedResult =
    completedEvent != null && 'result' in completedEvent
      ? completedEvent.result
      : undefined
  const finalState = parseApplicabilityState(completedResult ?? workflow.result)

  if (finalState != null) {
    for (const law of DISCOVER_LAW_ORDER) {
      const applicability = finalState[law]
      if (applicability == null) {
        continue
      }

      verdicts[law] = {
        law,
        skipped: applicability.source === 'USER_FORCE',
        status: applicability.status,
      }
    }
  }

  return verdicts
}

export function isProductDiscoverWorkflowRunning(
  workflow: LatestWorkflowForEntityResponse | undefined
): boolean {
  return workflow?.status === 'running'
}
